import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, Search, ShieldCheck, ShieldX, AlertTriangle,
  CheckCircle, XCircle, FileText, CreditCard,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface WarrantyResult {
  inWarranty: boolean;
  warrantyEndDate?: string;
  warrantyType?: string;
  productType?: string;
  serialNumber?: string;
  customerName?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  signsOfMisuse?: string[];
}

export function WarrantyVerificationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [serialNumber, setSerialNumber] = useState('');
  const [result, setResult] = useState<WarrantyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [misuseCheck, setMisuseCheck] = useState({
    hardWaterDamage: false,
    dryHeating: false,
    physicalDamage: false,
    unauthorizedRepair: false,
  });

  const [showPaidService, setShowPaidService] = useState(false);
  const [paidServiceForm, setPaidServiceForm] = useState({
    estimateAmount: '',
    advancePaid: '',
    paymentMode: '',
    notes: '',
  });
  const [submittingPaid, setSubmittingPaid] = useState(false);

  const handleWarrantySearch = async () => {
    if (!serialNumber.trim()) return;
    setSearching(true);
    setError(null);
    try {
      const res = await api.post(`/service-sop/complaints/${id}/warranty-verify`, {
        serialNumber: serialNumber.trim(),
      });
      setResult(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Warranty lookup failed');
      setResult(null);
    } finally {
      setSearching(false);
    }
  };

  const advanceToWarrantyCheck = async () => {
    if (!id) return;
    setLoading(true);
    try {
      await api.post(`/service-sop/complaints/${id}/warranty-verify`, {
        serialNumber,
        misuseCheck,
        proceedWith: result?.inWarranty ? 'warranty' : 'paid_service',
      });
      navigate(`/service-tickets/${id}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to proceed');
    } finally {
      setLoading(false);
    }
  };

  const handlePaidServiceSubmit = async () => {
    if (!id) return;
    setSubmittingPaid(true);
    try {
      await api.post(`/service-sop/complaints/${id}/paid-service`, {
        estimateAmount: parseFloat(paidServiceForm.estimateAmount) || 0,
        advancePaid: parseFloat(paidServiceForm.advancePaid) || 0,
        paymentMode: paidServiceForm.paymentMode,
        notes: paidServiceForm.notes,
      });
      navigate(`/service-tickets/${id}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to record paid service');
    } finally {
      setSubmittingPaid(false);
    }
  };

  const allMisuseChecked = Object.values(misuseCheck).every(v => v === false);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Warranty Verification</h1>
        <p className="text-sm text-[var(--text-muted)]">Verify product warranty status and check for misuse</p>
      </div>

      {/* Serial Number Search */}
      <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6 mb-6">
        <div className="flex gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="Enter product serial number..."
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              onKeyDown={(e) => e.key === 'Enter' && handleWarrantySearch()}
            />
          </div>
          <button
            onClick={handleWarrantySearch}
            disabled={searching || !serialNumber.trim()}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
          >
            {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Verify
          </button>
        </div>

        {error && (
          <div className="mt-3 flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle size={14} />
            {error}
          </div>
        )}
      </div>

      {/* Warranty Result */}
      {result && (
        <div className="rounded-xl border bg-[var(--bg-surface)] p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            {result.inWarranty ? (
              <>
                <ShieldCheck size={24} className="text-green-500" />
                <div>
                  <h3 className="font-semibold text-green-700">In Warranty</h3>
                  <p className="text-xs text-green-600">Valid until {formatDate(result.warrantyEndDate)}</p>
                </div>
              </>
            ) : (
              <>
                <ShieldX size={24} className="text-red-500" />
                <div>
                  <h3 className="font-semibold text-red-700">Out of Warranty</h3>
                  <p className="text-xs text-red-600">Paid service required</p>
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-[var(--text-muted)]">Product:</span>
              <span className="ml-2 font-medium">{result.productType}</span>
            </div>
            <div>
              <span className="text-[var(--text-muted)]">Serial:</span>
              <span className="ml-2 font-mono">{result.serialNumber}</span>
            </div>
            {result.invoiceNumber && (
              <div>
                <span className="text-[var(--text-muted)]">Invoice:</span>
                <span className="ml-2">{result.invoiceNumber}</span>
              </div>
            )}
            {result.invoiceDate && (
              <div>
                <span className="text-[var(--text-muted)]">Invoice Date:</span>
                <span className="ml-2">{formatDate(result.invoiceDate)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Misuse Check - Only show if in warranty */}
      {result?.inWarranty && (
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6 mb-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4">Check for Signs of Misuse</h3>
          <div className="space-y-3">
            {[
              { key: 'hardWaterDamage', label: 'Hard Water Damage' },
              { key: 'dryHeating', label: 'Dry Heating / Running without water' },
              { key: 'physicalDamage', label: 'Physical Damage' },
              { key: 'unauthorizedRepair', label: 'Unauthorized Repair' },
            ].map((item) => (
              <label key={item.key} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={misuseCheck[item.key as keyof typeof misuseCheck]}
                  onChange={(e) => setMisuseCheck((p) => ({ ...p, [item.key]: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-[var(--ink-0)]"
                />
                <span className="text-sm text-[var(--text-secondary)]">{item.label}</span>
                {misuseCheck[item.key as keyof typeof misuseCheck] && (
                  <XCircle size={14} className="text-red-500" />
                )}
              </label>
            ))}
          </div>

          {!allMisuseChecked && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <AlertTriangle size={14} className="inline mr-1" />
              Misuse detected — warranty claim may be rejected
            </div>
          )}
        </div>
      )}

      {/* Paid Service Form - Only show if out of warranty */}
      {result && !result.inWarranty && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard size={20} className="text-amber-600" />
            <h3 className="font-semibold text-amber-800">Paid Service Flow</h3>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-amber-800">Estimate Amount (₹)</label>
                <input
                  type="number"
                  value={paidServiceForm.estimateAmount}
                  onChange={(e) => setPaidServiceForm((f) => ({ ...f, estimateAmount: e.target.value }))}
                  placeholder="0.00"
                  className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-[var(--text-primary)] focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-amber-800">Advance Paid (₹)</label>
                <input
                  type="number"
                  value={paidServiceForm.advancePaid}
                  onChange={(e) => setPaidServiceForm((f) => ({ ...f, advancePaid: e.target.value }))}
                  placeholder="0.00"
                  className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-[var(--text-primary)] focus:border-amber-500 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-amber-800">Payment Mode</label>
              <select
                value={paidServiceForm.paymentMode}
                onChange={(e) => setPaidServiceForm((f) => ({ ...f, paymentMode: e.target.value }))}
                className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-[var(--text-primary)] focus:border-amber-500 focus:outline-none"
              >
                <option value="">Select mode</option>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="card">Card</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-amber-800">Notes</label>
              <textarea
                value={paidServiceForm.notes}
                onChange={(e) => setPaidServiceForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
                placeholder="Additional notes..."
                className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-amber-500 focus:outline-none"
              />
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      {result && (
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(`/service-tickets/${id}`)}
            className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]"
          >
            Back
          </button>
          {result.inWarranty ? (
            <button
              onClick={advanceToWarrantyCheck}
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              Proceed with Warranty Service
            </button>
          ) : (
            <button
              onClick={handlePaidServiceSubmit}
              disabled={submittingPaid || !paidServiceForm.estimateAmount}
              className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {submittingPaid ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              Collect Payment & Proceed
            </button>
          )}
        </div>
      )}
    </div>
  );
}